import { t as translate } from "../preferences";
import { formatQuantity } from "../numberFormat";
import { Camera, Flashlight, RefreshCw, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { haptic } from "../haptic";
import { useAppEnvironment } from "../../telegram";
import { api, type ProductOut } from "../api";
import { messageOf } from "./DataState";
import { useLocalBack } from "../navigation";

type ScannerControls = { stop(): void; switchTorch?: (on: boolean) => Promise<void> };

export function BarcodeProductPicker({
  products,
  onPick,
  requireStock = false,
  searchLabel,
}: {
  products: ProductOut[];
  onPick: (product: ProductOut) => void;
  requireStock?: boolean;
  searchLabel?: string;
}) {
  const [query, setQuery] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraState, setCameraState] = useState<"idle" | "requesting" | "scanning" | "denied" | "unsupported" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [torch, setTorch] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const controls = useRef<ScannerControls | null>(null);
  const accepted = useRef(false);
  const epoch = useRef(0);
  const { isActive } = useAppEnvironment();
  useEffect(() => { if (!isActive) closeCamera(); }, [isActive]);
  useLocalBack(cameraOpen, closeCamera);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("uz");
    return products.filter((product) => {
      if (requireStock && Number(product.joriy_ostatka) <= 0) return false;
      return !q || [product.nomi, product.artikul, product.barcode]
        .some((value) => value?.toLocaleLowerCase("uz").includes(q));
    });
  }, [products, query, requireStock]);

  function stopCamera() {
    ++epoch.current;
    controls.current?.stop();
    controls.current = null;
    const stream = video.current?.srcObject;
    if (typeof MediaStream !== "undefined" && stream instanceof MediaStream) stream.getTracks().forEach((track) => track.stop());
    if (video.current) video.current.srcObject = null;
    setTorch(false);
  }

  useEffect(() => () => stopCamera(), []);
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === "hidden") closeCamera(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  async function acceptBarcode(raw: string) {
    if (accepted.current) return;
    accepted.current = true;
    stopCamera();
    const request = epoch.current;
    try {
      const product = await api.productByBarcode(raw.trim());
      if (request !== epoch.current) return;
      if (!product.faol) throw new Error(translate(`${product.nomi} faol emas`, `${product.nomi}: товар неактивен`));
      if (requireStock && Number(product.joriy_ostatka) <= 0) throw new Error(translate(`${product.nomi} omborda mavjud emas`, `${product.nomi}: нет на складе`));
      onPick(product);
      haptic("success");
      setCameraOpen(false);
      setCameraState("idle");
    } catch (scanError) {
      if (request !== epoch.current) return;
      setError(messageOf(scanError));
      setCameraState("error");
      haptic("error");
    } finally {
      accepted.current = false;
    }
  }

  async function startCamera() {
    stopCamera();
    const request = epoch.current;
    setCameraOpen(true);
    setError(null);
    accepted.current = false;
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setCameraState("unsupported");
      return;
    }
    setCameraState("requesting");
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      if (!video.current || request !== epoch.current) return;
      const reader = new BrowserMultiFormatReader(undefined, {
        delayBetweenScanAttempts: 250,
        delayBetweenScanSuccess: 1000,
      });
      const nextControls = await reader.decodeFromConstraints(
        { audio: false, video: { facingMode: { ideal: "environment" } } },
        video.current,
        (result) => { if (result && request === epoch.current) void acceptBarcode(result.getText()); },
      );
      if (request !== epoch.current) { nextControls.stop(); return; }
      controls.current = nextControls;
      setCameraState("scanning");
    } catch (cameraError) {
      if (request !== epoch.current) return;
      stopCamera();
      const name = cameraError instanceof DOMException ? cameraError.name : "";
      if (name === "NotAllowedError" || name === "SecurityError") setCameraState("denied");
      else if (name === "NotFoundError" || name === "TypeError") setCameraState("unsupported");
      else setCameraState("error");
      setError(name === "NotReadableError" ? translate("Kamera band yoki ishga tushmadi.") : messageOf(cameraError));
    }
  }

  function closeCamera() {
    stopCamera();
    setCameraOpen(false);
    setCameraState("idle");
    setError(null);
  }

  async function toggleTorch() {
    if (!controls.current?.switchTorch) return;
    const next = !torch;
    try { await controls.current.switchTorch(next); setTorch(next); } catch { /* optional */ }
  }

  return <section className="m-space-y-3">
    <div className="m-flex m-gap-2">
      <label className="m-relative m-min-w-0 m-flex-1">
        <span className="m-sr-only">{searchLabel ?? translate("Tovar yoki shtrix-kod qidirish")}</span>
        <Search className="m-pointer-events-none m-absolute m-left-3 m-top-3.5 m-size-5 m-text-muted" />
        <input className="m-text-input m-pl-10" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={translate("Nomi, artikul yoki shtrix-kod")} />
      </label>
      <button type="button" className="m-touch-icon m-border m-border-border m-bg-surface" aria-label={translate("Shtrix-kodni skanerlash")} onClick={() => void startCamera()}>
        <Camera className="m-size-5" />
      </button>
    </div>

    {cameraOpen ? <div className="m-rounded-2xl m-border m-border-border m-bg-black m-p-2 m-text-white">
      <div className="m-flex m-items-center m-justify-between m-gap-2 m-p-1">
        <p className="m-text-sm m-font-bold">{translate("Shtrix-kod skaneri")}</p>
        <div className="m-flex m-gap-1">
          {cameraState === "scanning" && controls.current?.switchTorch ? <button type="button" className="m-touch-icon" aria-label={translate("Chiroq")} aria-pressed={torch} onClick={() => void toggleTorch()}><Flashlight className="m-size-5" /></button> : null}
          <button type="button" className="m-touch-icon" aria-label={translate("Skanerni yopish")} onClick={closeCamera}><X className="m-size-5" /></button>
        </div>
      </div>
      <video ref={video} muted playsInline className={`m-aspect-[4/3] m-w-full m-rounded-xl m-object-cover ${cameraState === "scanning" ? "" : "m-hidden"}`} />
      {cameraState === "requesting" ? <p className="m-p-5 m-text-center m-text-sm">{translate("Kamera ruxsati kutilmoqda…")}</p> : null}
      {cameraState === "denied" ? <ScannerMessage title={translate("Kameraga ruxsat berilmadi")} body={translate("Telegram yoki telefon sozlamalarida kamera ruxsatini yoqing. Quyidagi qo‘lda qidiruv ishlashda davom etadi.")} retry={() => void startCamera()} /> : null}
      {cameraState === "unsupported" ? <ScannerMessage title={translate("Kamera skaneri mavjud emas")} body={translate("Bu WebView kamera skanerini qo‘llamaydi. Tovarni nomi yoki shtrix-kodi bilan qo‘lda toping.")} /> : null}
      {cameraState === "error" ? <ScannerMessage title={translate("Skanerlash amalga oshmadi")} body={error ?? translate("Kamerani qayta ishga tushiring yoki qo‘lda qidiring.")} retry={() => void startCamera()} /> : null}
    </div> : null}

    <div className="m-max-h-64 m-space-y-2 m-overflow-y-auto" aria-label={translate("Tovarlar")}>
      {!cameraOpen && error ? <p className="m-error-box" role="alert">{error}</p> : null}
      {filtered.map((product) => <button key={product.id} type="button" className="m-flex m-min-h-14 m-w-full m-items-center m-justify-between m-gap-3 m-rounded-xl m-border m-border-border m-bg-surface m-p-3 m-text-left" onClick={() => onPick(product)}>
        <span className="m-min-w-0"><strong className="m-block m-truncate">{product.nomi}</strong><span className="m-block m-truncate m-text-xs m-text-muted">{product.barcode || product.artikul || translate("Kod yo‘q")}</span></span>
          <span className="m-shrink-0 m-text-sm m-font-bold m-text-primary">{formatQuantity(product.joriy_ostatka)} {product.birlik}</span>
      </button>)}
      {!filtered.length ? <div className="m-rounded-xl m-bg-elevated m-p-4 m-text-sm m-text-muted"><p>{translate("Mos tovar topilmadi.")}</p>{query.trim() ? <button type="button" className="m-secondary-button m-mt-3" onClick={() => void acceptBarcode(query)}>{translate("Shtrix-kodni serverdan qidirish")}</button> : <p className="m-mt-1">{translate("Nomi yoki shtrix-kodni qo‘lda kiriting.")}</p>}</div> : null}
    </div>
  </section>;
}

function ScannerMessage({ title, body, retry }: { title: string; body: string; retry?: () => void }) {
  return <div className="m-space-y-3 m-p-4 m-text-center"><strong className="m-block">{title}</strong><p className="m-text-sm m-text-white/75">{body}</p>{retry ? <button type="button" className="m-secondary-button m-bg-white m-text-black" onClick={retry}><RefreshCw className="m-size-4" />{translate("Qayta urinish")}</button> : null}</div>;
}
